import { Group } from './types'

function isYouTubeUrl(input: string): boolean {
  try {
    const url = new URL(input);
    return url.hostname.includes('youtube.com') || url.hostname.includes('youtu.be');
  } catch {
    return false;
  }
}

function parseYouTubeIdAndParams(input: string): { id: string | null; start?: number; list?: string } {
  try {
    const url = new URL(input);
    let id: string | null = null;
    let start: number | undefined;
    let list: string | undefined;

    if (url.hostname.includes('youtu.be')) {
      // e.g. https://youtu.be/VIDEO_ID
      id = url.pathname.replace(/^\//, '') || null;
      list = url.searchParams.get('list') || undefined;
      const t = url.searchParams.get('t');
      if (t) start = parseYouTubeTimeToSeconds(t);
    } else if (url.hostname.includes('youtube.com')) {
      if (url.pathname.startsWith('/watch')) {
        id = url.searchParams.get('v');
        list = url.searchParams.get('list') || undefined;
        const t = url.searchParams.get('t');
        if (t) start = parseYouTubeTimeToSeconds(t);
      } else if (url.pathname.startsWith('/shorts/')) {
        // e.g. https://www.youtube.com/shorts/VIDEO_ID
        const segs = url.pathname.split('/').filter(Boolean);
        id = segs[1] || null;
      } else if (url.pathname.startsWith('/embed/')) {
        const segs = url.pathname.split('/').filter(Boolean);
        id = segs[1] || null;
      }
    }

    return { id: id || null, start, list };
  } catch {
    return { id: null };
  }
}

function parseYouTubeTimeToSeconds(t: string): number {
  // supports formats like 90, 1m30s, 2h1m, 2h1m3s
  if (/^\d+$/.test(t)) return parseInt(t, 10);
  const re = /(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/i;
  const match = t.match(re);
  if (!match) return 0;
  const hours = parseInt(match[1] || '0', 10);
  const minutes = parseInt(match[2] || '0', 10);
  const seconds = parseInt(match[3] || '0', 10);
  return hours * 3600 + minutes * 60 + seconds;
}

function toYouTubeEmbedSrc(input: string): string | null {
  const { id, start, list } = parseYouTubeIdAndParams(input);
  if (!id) return null;
  const params = new URLSearchParams();
  if (typeof start === 'number' && start > 0) params.set('start', String(start));
  if (list) params.set('list', list);
  const qs = params.toString();
  return `https://www.youtube.com/embed/${id}${qs ? `?${qs}` : ''}`;
}

export const GROUPS: Group[] = [
  {
    name: 'ai',
    title: 'AI',
    commands: [
      {
        name: 'askAi',
        label: 'Ask AI',
        iconName: 'MessageSquare',
        description: 'Open AI chat for help and questions',
        aliases: ['ask', 'ai', 'chat'],
        handlesDeletion: true,
        action: (editor, range) => {
          // Delete the slash command text
          if (range) {
            editor.chain().focus().deleteRange(range).run()
          }
          // Open chat sidebar directly
          // @ts-expect-error - custom event
          editor.emit('openChat', {})
        },
      },
      {
        name: 'generateSummary',
        label: 'Generate Summary',
        iconName: 'FileText',
        description: 'Create a summary of the document',
        aliases: ['summary', 'summarize'],
        action: (editor) => {
          // @ts-expect-error - custom event
          editor.emit('openAiCommand', { commandName: 'generateSummary' })
        },
      },
      {
        name: 'generateFaq',
        label: 'Generate FAQ',
        iconName: 'Sparkles',
        description: 'Generate frequently asked questions',
        aliases: ['faq', 'questions'],
        action: (editor) => {
          // @ts-expect-error - custom event
          editor.emit('openAiCommand', { commandName: 'generateFaq' })
        },
      },
    ],
  },
  {
    name: 'format',
    title: 'Format',
    commands: [
      {
        name: 'heading2',
        label: 'Heading 2',
        iconName: 'Heading2',
        description: 'Medium priority section title',
        aliases: ['h2'],
        action: (editor) => {
          editor.chain().focus().setHeading({ level: 2 }).run()
        },
      },
      {
        name: 'heading3',
        label: 'Heading 3',
        iconName: 'Heading3',
        description: 'Low priority section title',
        aliases: ['h3'],
        action: (editor) => {
          editor.chain().focus().setHeading({ level: 3 }).run()
        },
      },
      {
        name: 'bulletList',
        label: 'Bullet List',
        iconName: 'List',
        description: 'Unordered list of items',
        aliases: ['ul'],
        action: (editor) => {
          editor.chain().focus().toggleBulletList().run()
        },
      },
      {
        name: 'numberedList',
        label: 'Numbered List',
        iconName: 'ListOrdered',
        description: 'Ordered list of items',
        aliases: ['ol'],
        action: (editor) => {
          editor.chain().focus().toggleOrderedList().run()
        },
      },
      {
        name: 'taskList',
        label: 'Task List',
        iconName: 'ListTodo',
        description: 'Task list with todo items',
        aliases: ['todo'],
        action: (editor) => {
          editor.chain().focus().toggleTaskList().run()
        },
      },
      // {
      //   name: 'toggleList',
      //   label: 'Toggle List',
      //   iconName: 'ListCollapse',
      //   description: 'Toggles can show and hide content',
      //   aliases: ['toggle'],
      //   action: editor => {
      //     editor.chain().focus().setDetails().run()
      //   },
      // },
      {
        name: 'blockquote',
        label: 'Blockquote',
        iconName: 'Quote',
        description: 'Element for quoting',
        action: (editor) => {
          editor.chain().focus().setBlockquote().run()
        },
      },
      {
        name: 'codeBlock',
        label: 'Code Block',
        iconName: 'CodeXml',
        description: 'Code block with syntax highlighting',
        shouldBeHidden: (editor) => editor.isActive('columns'),
        action: (editor) => {
          editor.chain().focus().setCodeBlock().run()
        },
      },
    ],
  },
  {
    name: 'inline',
    title: 'Inline',
    commands: [
      {
        name: 'button',
        label: 'Button',
        iconName: 'RectangleHorizontal',
        description: 'Insert an inline button',
        aliases: ['btn'],
        isInlineCommand: true,
        action: (editor) => {
          const selection = editor.state.selection
          const selectedText = editor.state.doc.textBetween(selection.from, selection.to, ' ')
          editor.chain().focus().insertInlineButton({
            text: selectedText || 'New Button',
            variant: 'primary',
          }).run()
        },
      },
      {
        name: 'inlineImage',
        label: 'Inline Image',
        iconName: 'ImagePlus',
        description: 'Insert an inline image',
        aliases: ['inline-img', 'emoji', 'inline-image'],
        isInlineCommand: true,
        handlesDeletion: true,
        action: (editor, range) => {
          // @ts-expect-error - custom event
          editor.emit('openInlineImageDialog', { range })
        },
      },
      {
        name: 'link',
        label: 'Link',
        iconName: 'Link',
        description: 'Insert a link',
        aliases: ['url', 'href'],
        isInlineCommand: true,
        handlesDeletion: true,
        action: (editor, range) => {
          // @ts-expect-error - custom event  
          editor.emit('openLinkDialog', { range })
        },
      },
      {
        name: 'emoji',
        label: 'Emoji',
        iconName: 'Smile',
        description: 'Insert an emoji',
        aliases: ['emoticon', 'smiley'],
        isInlineCommand: true,
        action: (editor) => {
          // Insert : to trigger the emoji dropdown menu
          editor.chain().focus().insertContent(':').run()
        },
      },
    ],
  },
  {
    name: 'insert',
    title: 'Insert',
    commands: [
      {
        name: 'internalNote',
        label: 'Internal Note',
        iconName: 'StickyNote',
        description: 'Private note for the team (not published)',
        aliases: ['internal', 'note', 'private'],
        action: editor => {
          editor.chain().focus().insertInternalNote().run()
        },
      },
      {
        name: 'image',
        label: 'Image',
        iconName: 'Image',
        description: 'Insert an image',
        aliases: ['img', 'photo', 'picture'],
        action: editor => {
          editor.chain().focus().setImageUploadNode().run()
        },
      },
      {
        name: 'docVideo',
        label: 'Video for this Doc',
        iconName: 'Video',
        description: 'View the document video',
        aliases: ['doc-video', 'document-video'],
        action: (editor) => {
          // @ts-expect-error - custom event
          editor.emit('insertDocVideo', {})
        },
      },
      {
        name: 'video',
        label: 'Video from URL',
        iconName: 'Video',
        description: 'Insert a video from URL',
        aliases: ['vid', 'movie', 'youtube'],
        action: (editor) => {
          // Prompt for video URL
          const url = window.prompt('Enter video URL:')
          if (url) {
            const trimmed = url.trim()
            if (isYouTubeUrl(trimmed)) {
              const embedSrc = toYouTubeEmbedSrc(trimmed)
              if (embedSrc) {
                editor.chain().focus().insertContent({
                  type: 'youtube',
                  attrs: {
                    src: embedSrc,
                  },
                }).run()
                return
              }
            }
            editor.chain().focus().insertVideo({
              src: trimmed,
              width: '100%',
              height: 'auto',
              align: 'center',
            }).run()
          }
        },
      },
      {
        name: 'table',
        label: 'Table',
        iconName: 'Table',
        description: 'Insert a table',
        shouldBeHidden: (editor) => editor.isActive('columns'),
        action: (editor) => {
          editor
            .chain()
            .focus()
            .insertTable({ rows: 3, cols: 3, withHeaderRow: false })
            .run()
        },
      },
      {
        name: 'columns',
        label: 'Columns',
        iconName: 'Columns2',
        description: 'Add two column content',
        aliases: ['cols'],
        shouldBeHidden: (editor) => editor.isActive('columns'),
        action: (editor) => {
          editor
            .chain()
            .focus()
            .setColumns()
            .focus(editor.state.selection.head - 1)
            .run()
        },
      },
      {
        name: 'horizontalRule',
        label: 'Horizontal Rule',
        iconName: 'Minus',
        description: 'Insert a horizontal divider',
        aliases: ['hr'],
        action: (editor) => {
          editor.chain().focus().setHorizontalRule().run()
        },
      },
      {
        name: 'mermaid',
        label: 'Mermaid Diagram',
        iconName: 'GitGraph',
        description: 'Insert a Mermaid diagram',
        aliases: ['diagram', 'flowchart', 'graph'],
        shouldBeHidden: (editor) => editor.isActive('columns'),
        action: (editor) => {
          editor.chain().focus().insertMermaid().run()
        },
      },
    ],
  },
  {
    name: 'admonition',
    title: 'Admonition',
    commands: [
      {
        name: 'note',
        label: 'Note',
        iconName: 'Lightbulb',
        description: 'Insert a note',
        aliases: ['note'],
        action: (editor) => {
          editor
            .chain()
            .focus()
            .insertAdmonition({
              admonitionType: 'note',
            })
            .run()
        },
      },
      {
        name: 'tip',
        label: 'Tip',
        iconName: 'Check',
        description: 'Insert a tip',
        aliases: ['tip'],
        action: (editor) => {
          editor
            .chain()
            .focus()
            .insertAdmonition({
              admonitionType: 'tip',
            })
            .run()
        },
      },
      {
        name: 'info',
        label: 'Info',
        iconName: 'Info',
        description: 'Insert an info',
        aliases: ['info'],
        action: (editor) => {
          editor
            .chain()
            .focus()
            .insertAdmonition({
              admonitionType: 'info',
            })
            .run()
        },
      },
      {
        name: 'warning',
        label: 'Warning',
        iconName: 'CircleAlert',
        description: 'Insert a warning',
        aliases: ['warning'],
        action: (editor) => {
          editor
            .chain()
            .focus()
            .insertAdmonition({
              admonitionType: 'warning',
            })
            .run()
        },
      },
      {
        name: 'danger',
        label: 'Danger',
        iconName: 'TriangleAlert',
        description: 'Insert a danger',
        aliases: ['danger'],
        action: (editor) => {
          editor
            .chain()
            .focus()
            .insertAdmonition({
              admonitionType: 'danger',
            })
            .run()
        },
      },
    ],
  },
  {
    name: 'container',
    title: 'Container',
    commands: [
      {
        name: 'accordion',
        label: 'Accordion',
        iconName: 'PanelTop',
        description: 'Insert an accordion',
        aliases: ['accordion', 'faq'],
        action: editor => {
          editor.chain().focus().insertAccordion().run()
        },
      },
      {
        name: 'accordionGroup',
        label: 'Accordion Group',
        iconName: 'GalleryVertical',
        description: 'Insert an accordion group',
        aliases: ['accordion', 'group', 'faq'],
        action: editor => {
          editor.chain().focus().insertAccordionGroup().run()
        },
      },
      {
        name: 'card',
        label: 'Card',
        iconName: 'CreditCard',
        description: 'Insert a card',
        aliases: ['card'],
        action: (editor) => {
          editor.chain().focus().insertCard().run()
        },
      },
      {
        name: 'stepGroup',
        label: 'Steps',
        iconName: 'LayoutList',
        description: 'Insert step-by-step guide',
        aliases: ['step', 'guide'],
        action: editor => {
          editor.chain().focus().insertSteps().run()
        },
      },
      {
        name: 'tab',
        label: 'Tabs',
        iconName: 'AppWindow',
        description: 'Insert a tab',
        aliases: ['tab'],
        action: (editor) => {
          editor.chain().focus().insertTabs().run()
        },
      },
      {
        name: 'folderContents',
        label: 'Folder Contents',
        iconName: 'Folder',
        aliases: ['folder'],
        description: 'Insert folder contents',
        //shouldBeHidden: editor => editor.isActive('columns'),
        action: (editor) => {
          editor.chain().focus().insertFolderContents({}).run()
        },
      },
    ],
  },
]

export default GROUPS
